import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Search, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useAuth } from '@/hooks/useAuth';
import { useSubmitProposal, ProposalType } from '@/hooks/useProposals';
import { useDataStandards } from '@/hooks/useDataStandards';
import { useAdfElementTree, flattenTree, getElementPath, AdfElementNode } from '@/hooks/useAdfStandard';
import { useFormProtection } from '@/hooks/useFormProtection';
import { CharacterCounter } from '@/components/shared/CharacterCounter';
import { sanitizeText, titleSchema, longTextSchema, nameSchema, emailSchema, organizationSchema, fieldNameSchema, enumValueSchema } from '@/lib/validation';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const proposalTypes: { value: ProposalType; label: string; description: string }[] = [
  { value: 'field', label: 'New Field', description: 'Propose a new data field' },
  { value: 'enum_value', label: 'New Enum Value', description: 'Add value to an enum' },
  { value: 'standard', label: 'New Standard', description: 'Propose a new data standard' },
  { value: 'adf_element', label: 'ADF Element', description: 'Propose a new ADF XML element' },
  { value: 'adf_mapping', label: 'ADF Mapping', description: 'Map a field to ADF element' },
  { value: 'adf_attribute', label: 'ADF Attribute', description: 'Add attribute to ADF element' },
];

const proposalSchema = z.object({
  proposal_type: z.enum(['field', 'enum_value', 'standard', 'adf_element', 'adf_mapping', 'adf_attribute']),
  title: titleSchema,
  description: longTextSchema,
  justification: longTextSchema,
  proposed_field_name: fieldNameSchema,
  proposed_field_type: z.string().optional(),
  proposed_field_format: z.string().optional(),
  proposed_field_required: z.boolean().optional(),
  proposed_enum_value: enumValueSchema,
  target_field_name: z.string().max(50).optional(),
  target_standard_id: z.string().optional(),
  target_adf_element_id: z.string().optional(),
  target_category: z.string().max(50).optional(),
  submitter_name: nameSchema,
  submitter_email: emailSchema,
  submitter_organization: organizationSchema,
});

type ProposalFormData = z.infer<typeof proposalSchema>;

interface ProposalFormProps {
  formId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** If true, hides the inline action buttons (parent will render them) */
  hideActions?: boolean;
}

const fieldDataTypes = [
  'string',
  'number',
  'integer',
  'boolean',
  'array',
  'object',
];

const fieldFormats = [
  'date',
  'date-time',
  'email',
  'uri',
  'uuid',
  'phone',
  'currency',
];

export function ProposalForm({ formId, onSuccess, onCancel, hideActions = false }: ProposalFormProps) {
  const { user, profile } = useAuth();
  const { schemas } = useDataStandards();
  const { data: adfTree = [] } = useAdfElementTree();
  const submitMutation = useSubmitProposal();
  const [proposalType, setProposalType] = useState<ProposalType>('field');
  const [elementSelectorOpen, setElementSelectorOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState<AdfElementNode | null>(null);
  const { honeypotProps, validateSubmission, recordSubmission, isRateLimited } = useFormProtection('proposal');

  // Flatten tree for the selector
  const flatElements = useMemo(() => flattenTree(adfTree), [adfTree]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProposalFormData>({
    resolver: zodResolver(proposalSchema),
    defaultValues: {
      proposal_type: 'field',
      proposed_field_required: false,
      submitter_name: profile?.full_name || '',
      submitter_email: user?.email || '',
    },
  });

  const selectedStandardId = watch('target_standard_id');
  const isAdfType = ['adf_element', 'adf_mapping', 'adf_attribute'].includes(proposalType);

  const onSubmit = async (data: ProposalFormData) => {
    // Check honeypot and rate limiting
    const protection = validateSubmission();
    if (!protection.valid) {
      toast.error(protection.error || 'Submission failed. Please try again.');
      return;
    }

    try {
      // Sanitize all text inputs before submission
      await submitMutation.mutateAsync({
        proposal_type: data.proposal_type,
        title: sanitizeText(data.title),
        description: sanitizeText(data.description),
        justification: sanitizeText(data.justification),
        submitter_name: sanitizeText(data.submitter_name),
        submitter_email: data.submitter_email.trim().toLowerCase(),
        submitter_organization: data.submitter_organization ? sanitizeText(data.submitter_organization) : undefined,
        proposed_field_name: data.proposed_field_name?.trim(),
        proposed_field_type: data.proposed_field_type,
        proposed_field_format: data.proposed_field_format,
        proposed_field_required: data.proposed_field_required,
        proposed_enum_value: data.proposed_enum_value?.trim(),
        target_field_name: data.target_field_name?.trim(),
        target_standard_id: data.target_standard_id,
        target_adf_element_id: data.target_adf_element_id,
        target_category: data.target_category?.trim(),
      });
      recordSubmission();
      toast.success('Proposal submitted successfully!');
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to submit proposal. Please try again.');
    }
  };

  const handleTypeChange = (value: ProposalType) => {
    setProposalType(value);
    setValue('proposal_type', value);
    // Clear type-specific fields when changing type
    setSelectedElement(null);
    setValue('target_adf_element_id', undefined);
  };

  const handleElementSelect = (element: AdfElementNode) => {
    setSelectedElement(element);
    setValue('target_adf_element_id', element.id);
    setElementSelectorOpen(false);
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Honeypot field - hidden from humans, catches bots */}
      <input type="text" {...honeypotProps} />
      
      {/* Rate limit warning */}
      {isRateLimited && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          Too many submissions. Please wait a moment before trying again.
        </div>
      )}

      {/* Proposal Type */}
      <div className="space-y-2">
        <Label>Proposal Type *</Label>
        <Select value={proposalType} onValueChange={handleTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Select proposal type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="field">New Field</SelectItem>
            <SelectItem value="enum_value">New Enum Value</SelectItem>
            <SelectItem value="standard">New Standard</SelectItem>
            <SelectItem value="adf_element">ADF Element</SelectItem>
            <SelectItem value="adf_mapping">ADF Field Mapping</SelectItem>
            <SelectItem value="adf_attribute">ADF Attribute</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {proposalTypes.find(t => t.value === proposalType)?.description}
        </p>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          placeholder={
            proposalType === 'field'
              ? 'e.g., New field: vehicle_acquisition_date'
              : proposalType === 'enum_value'
              ? "e.g., Add 'HYBRID' to fuel_type enum"
              : proposalType === 'standard'
              ? 'e.g., New standard: TradeAppraisal'
              : proposalType === 'adf_element'
              ? 'e.g., Add <tradein> element to prospect'
              : proposalType === 'adf_mapping'
              ? 'e.g., Map vin field to vehicle element'
              : 'e.g., Add type attribute to name element'
          }
          {...register('title')}
        />
        {errors.title && (
          <p className="text-sm text-destructive">{errors.title.message}</p>
        )}
      </div>

      {/* Target Standard (for field and enum proposals) */}
      {(proposalType === 'field' || proposalType === 'enum_value') && (
        <div className="space-y-2">
          <Label>Target Standard</Label>
          <Select
            value={selectedStandardId || ''}
            onValueChange={(value) => setValue('target_standard_id', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a standard (optional)" />
            </SelectTrigger>
            <SelectContent>
              {schemas.map((schema) => (
                <SelectItem key={schema.id} value={schema.id}>
                  {schema.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* ADF Element Selector (for ADF types) */}
      {isAdfType && (
        <div className="space-y-2">
          <Label>
            {proposalType === 'adf_element' ? 'Parent Element' : 'Target Element'} *
          </Label>
          <Popover open={elementSelectorOpen} onOpenChange={setElementSelectorOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={elementSelectorOpen}
                className="w-full justify-between font-normal"
              >
                {selectedElement ? (
                  <span className="flex items-center gap-2">
                    <code className="text-xs bg-muted px-1 rounded">
                      &lt;{selectedElement.tag_name}&gt;
                    </code>
                    <span className="text-muted-foreground text-xs truncate">
                      {getElementPath(selectedElement)}
                    </span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">Select an ADF element...</span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search elements..." />
                <CommandList>
                  <CommandEmpty>No element found.</CommandEmpty>
                  <CommandGroup>
                    {flatElements.map((element) => (
                      <CommandItem
                        key={element.id}
                        value={`${element.tag_name} ${element.display_name} ${getElementPath(element)}`}
                        onSelect={() => handleElementSelect(element)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedElement?.id === element.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-muted px-1 rounded">
                              &lt;{element.tag_name}&gt;
                            </code>
                            <span className="font-medium">{element.display_name}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {getElementPath(element)}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Field-specific fields */}
      {proposalType === 'field' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="proposed_field_name">Field Name *</Label>
              <Input
                id="proposed_field_name"
                placeholder="e.g., vehicle_acquisition_date"
                {...register('proposed_field_name')}
              />
            </div>
            <div className="space-y-2">
              <Label>Data Type</Label>
              <Select
                onValueChange={(value) => setValue('proposed_field_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {fieldDataTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Format</Label>
              <Select
                onValueChange={(value) => setValue('proposed_field_format', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select format (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {fieldFormats.map((format) => (
                    <SelectItem key={format} value={format}>
                      {format}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-7">
              <Switch
                id="required"
                onCheckedChange={(checked) => setValue('proposed_field_required', checked)}
              />
              <Label htmlFor="required" className="cursor-pointer">
                Required field
              </Label>
            </div>
          </div>
        </>
      )}

      {/* Enum-specific fields */}
      {proposalType === 'enum_value' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="proposed_enum_value">Enum Value *</Label>
            <Input
              id="proposed_enum_value"
              placeholder="e.g., HYBRID"
              {...register('proposed_enum_value')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="target_field_name">Target Field Name *</Label>
            <Input
              id="target_field_name"
              placeholder="e.g., fuel_type"
              {...register('target_field_name')}
            />
          </div>
        </div>
      )}

      {/* Standard-specific fields */}
      {proposalType === 'standard' && (
        <div className="space-y-2">
          <Label htmlFor="target_category">Category</Label>
          <Input
            id="target_category"
            placeholder="e.g., transactions, inventory, customer"
            {...register('target_category')}
          />
        </div>
      )}

      {/* ADF Element-specific fields */}
      {proposalType === 'adf_element' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="proposed_field_name">Tag Name *</Label>
            <Input
              id="proposed_field_name"
              placeholder="e.g., tradein"
              {...register('proposed_field_name')}
            />
            <p className="text-xs text-muted-foreground">
              XML tag name without brackets
            </p>
          </div>
          <div className="flex items-center gap-3 pt-7">
            <Switch
              id="required"
              onCheckedChange={(checked) => setValue('proposed_field_required', checked)}
            />
            <Label htmlFor="required" className="cursor-pointer">
              Required element
            </Label>
          </div>
        </div>
      )}

      {/* ADF Mapping-specific fields */}
      {proposalType === 'adf_mapping' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="proposed_field_name">Field Name *</Label>
            <Input
              id="proposed_field_name"
              placeholder="e.g., vin"
              {...register('proposed_field_name')}
            />
            <p className="text-xs text-muted-foreground">
              Name of the field from the Field Library
            </p>
          </div>
          <div className="flex items-center gap-3 pt-7">
            <Switch
              id="required"
              onCheckedChange={(checked) => setValue('proposed_field_required', checked)}
            />
            <Label htmlFor="required" className="cursor-pointer">
              Required mapping
            </Label>
          </div>
        </div>
      )}

      {/* ADF Attribute-specific fields */}
      {proposalType === 'adf_attribute' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="proposed_field_name">Attribute Name *</Label>
            <Input
              id="proposed_field_name"
              placeholder="e.g., type"
              {...register('proposed_field_name')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="proposed_enum_value">Valid Values</Label>
            <Input
              id="proposed_enum_value"
              placeholder="e.g., individual, business"
              {...register('proposed_enum_value')}
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated list of valid values
            </p>
          </div>
        </div>
      )}

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description *</Label>
        <Textarea
          id="description"
          placeholder="Describe what you're proposing and how it would be used..."
          rows={4}
          {...register('description')}
        />
        {errors.description && (
          <p className="text-sm text-destructive">{errors.description.message}</p>
        )}
      </div>

      {/* Justification */}
      <div className="space-y-2">
        <Label htmlFor="justification">Justification *</Label>
        <Textarea
          id="justification"
          placeholder="Why is this needed? What problem does it solve? What are the use cases?"
          rows={4}
          {...register('justification')}
        />
        {errors.justification && (
          <p className="text-sm text-destructive">{errors.justification.message}</p>
        )}
      </div>

      {/* Contact Information */}
      <div className="border-t pt-6 space-y-4">
        <h4 className="font-medium">Your Information</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="submitter_name">Name *</Label>
            <Input
              id="submitter_name"
              placeholder="Your name"
              {...register('submitter_name')}
            />
            {errors.submitter_name && (
              <p className="text-sm text-destructive">{errors.submitter_name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="submitter_email">Email *</Label>
            <Input
              id="submitter_email"
              type="email"
              placeholder="your@email.com"
              {...register('submitter_email')}
            />
            {errors.submitter_email && (
              <p className="text-sm text-destructive">{errors.submitter_email.message}</p>
            )}
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="submitter_organization">Organization</Label>
          <Input
            id="submitter_organization"
            placeholder="Your company or organization (optional)"
            {...register('submitter_organization')}
          />
        </div>
      </div>

      {/* Actions - only render if not hidden */}
      {!hideActions && (
        <div className="flex justify-end gap-3 pt-4">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={submitMutation.isPending}>
            {submitMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Submit Proposal
          </Button>
        </div>
      )}
    </form>
  );
}